import type { ReactNode } from "react"
import { Bell } from "lucide-react"
import {NavLink, useLocation} from "react-router-dom"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter, SidebarGroup, SidebarGroupContent, SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar"
import { subjectStatusLabels } from "@/lib/api/subjects"
import { useIsAdmin, useSession } from "@/store/session"

import { AppBreadcrumbs } from "./AppBreadcrumbs"
import { PageContainer, type PageContainerWidth } from "./PageContainer"
import { UserMenu } from "./UserMenu"
import {primaryNav, renderNav, settingsNav} from "@/layout/NavItems.tsx";

function SubjectStatusIndicator() {
  const subject = useSession((s) => s.subject)
  // Nothing to show before `RequireAuth`'s fetch resolves, or for a user
  // with no subject connected yet (e.g. mid `/connect-subject`) — the badge
  // has no state to represent in either case, so it renders nothing rather
  // than a placeholder.
  if (!subject) return null

  return (
    <span className="flex items-center gap-2">
      <span className="hidden text-sm text-muted-foreground sm:inline">
        {subject.nazva}
      </span>
      <Badge
        variant={subject.status === "ВИКЛЮЧЕНИЙ" ? "destructive" : "secondary"}
      >
        {subjectStatusLabels[subject.status]}
      </Badge>
    </span>
  )
}

/**
 * app-shell.md's authenticated layout frame — branding + sidebar rail, a
 * header (breadcrumb, subject status, notification placeholder, user menu —
 * no theme toggle: app-shell.md notes it was deliberately unmounted
 * app-wide), and a page content slot. `RequireAuth` wraps every protected
 * route in this.
 *
 * Sidebar navigation lists only real pages so far: most of the wireframe's
 * nav entries (Schedule, Warehouse, …) have no real page behind them yet in
 * this package, and its permission-gated items need a real
 * `hasPermission`/role source rather than the wireframe's demo one. Both
 * land together once there's something to link to and gate.
 */
export function AppShell({
  children,
  contentWidth = "default",
}: {
  children: ReactNode
  contentWidth?: PageContainerWidth
}) {
  const { pathname } = useLocation();
  const isAdmin = useIsAdmin();
  return (
    <div className="min-h-svh bg-sidebar print:min-h-0">
      <SidebarProvider className="app-shell-frame relative print:min-h-0">
        <Sidebar className="app-shell-sidebar print:hidden" collapsible="icon">
          <SidebarHeader>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  className="text-[oklch(0.84_0.04_268.46)] hover:text-[oklch(0.84_0.04_268.46)] active:text-[oklch(0.84_0.04_268.46)]"
                  size="lg"
                >
                  <NavLink to="/">
                    <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                      {/* The prototype's mark (otk/wireframes Shell.tsx): a
                        12-tooth gear with a check in its hole. `size-5!`
                        overrides SidebarMenuButton's `[&_svg]:size-4`. */}
                      <svg
                        aria-hidden="true"
                        className="size-5!"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10.46 2.51L10.71 0.47A11.6 11.6 0 0 1 13.29 0.47L13.54 2.51A9.62 9.62 0 0 1 15.42 3.01L16.65 1.37A11.6 11.6 0 0 1 18.88 2.66L18.08 4.55A9.62 9.62 0 0 1 19.45 5.92L21.34 5.12A11.6 11.6 0 0 1 22.63 7.35L20.99 8.58A9.62 9.62 0 0 1 21.49 10.46L23.53 10.71A11.6 11.6 0 0 1 23.53 13.29L21.49 13.54A9.62 9.62 0 0 1 20.99 15.42L22.63 16.65A11.6 11.6 0 0 1 21.34 18.88L19.45 18.08A9.62 9.62 0 0 1 18.08 19.45L18.88 21.34A11.6 11.6 0 0 1 16.65 22.63L15.42 20.99A9.62 9.62 0 0 1 13.54 21.49L13.29 23.53A11.6 11.6 0 0 1 10.71 23.53L10.46 21.49A9.62 9.62 0 0 1 8.58 20.99L7.35 22.63A11.6 11.6 0 0 1 5.12 21.34L5.92 19.45A9.62 9.62 0 0 1 4.55 18.08L2.66 18.88A11.6 11.6 0 0 1 1.37 16.65L3.01 15.42A9.62 9.62 0 0 1 2.51 13.54L0.47 13.29A11.6 11.6 0 0 1 0.47 10.71L2.51 10.46A9.62 9.62 0 0 1 3.01 8.58L1.37 7.35A11.6 11.6 0 0 1 2.66 5.12L4.55 5.92A9.62 9.62 0 0 1 5.92 4.55L5.12 2.66A11.6 11.6 0 0 1 7.35 1.37L8.58 3.01A9.62 9.62 0 0 1 10.46 2.51ZM5.36 12A6.64 6.64 0 1 0 18.64 12A6.64 6.64 0 1 0 5.36 12Z"
                        />
                        <path
                          d="M8.35 11.16L11.39 14.2L16.3 9.29"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2.11}
                        />
                      </svg>
                    </div>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold">
                        АРМ «Суб'єкт ОТК»
                      </span>
                    </div>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarHeader>
          <SidebarContent className="md:overflow-hidden">
            <SidebarGroup>
              <SidebarGroupLabel>Робочий простір</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {renderNav(primaryNav, pathname, isAdmin)}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
            <SidebarGroup>
              <SidebarGroupLabel>Налаштування</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {renderNav(settingsNav, pathname, isAdmin)}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>
          <SidebarFooter>
            <SidebarMenu>
              <SidebarMenuItem className="flex justify-end">
                <SidebarTrigger className="text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground" />
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarFooter>
        </Sidebar>
        <SidebarInset className="border-r print:border-0">
          <header className="flex h-14 shrink-0 items-center gap-2 border-b px-4 print:hidden">
            <AppBreadcrumbs />
            <SubjectStatusIndicator />
            <Button size="icon" title="Повідомлення" variant="ghost">
              <Bell />
            </Button>
            <UserMenu />
          </header>
          {/* Prototype offset (Shell.tsx): `pt-2`/`md:pt-3` plus PageContainer's
              8px gap after its always-present notice wrapper — 16/20px here,
              where no such wrapper exists. */}
          <main className="flex min-w-0 flex-1 flex-col bg-muted px-4 pt-4 pb-4 md:px-6 md:pt-5 md:pb-6 dark:bg-background print:p-0">
            <PageContainer width={contentWidth}>{children}</PageContainer>
          </main>
        </SidebarInset>
      </SidebarProvider>
    </div>
  )
}
